import React, { Component, useCallback, useEffect, useMemo } from 'react';
import { StaticSelect } from '@kineticdata/react';
import { getClearButtonWrapper, parseRenderOptions } from './utils';
import { FieldWrapper } from './FieldWrapper';
import classNames from 'classnames';
import { Map } from 'immutable';
import { Icon } from '../Icon';
import {
  generateInput,
  generateMultipleSelection,
  generateMultipleSelectionsContainer,
  generateSingleSelection,
  generateSingleSelectionsContainer,
  Status,
  SuggestionAction,
  SuggestionsContainer,
} from './TypeaheadDefaults';

/******************************************************************************
 * Typeahead Component
 ******************************************************************************/

const selectionToValue = selection =>
  selection.get('displayValue') ||
  selection.get('label') ||
  selection.get('value');

const Suggestion = ({ suggestion, active, custom }) => (
  <div className={classNames('suggestion', { active })}>
    {custom && <div className="small">Custom Option</div>}
    <div className="large">
      {suggestion.get('label') || suggestion.get('value')}
    </div>
    {suggestion.get('detail') && (
      <div className="small">{suggestion.get('detail')}</div>
    )}
  </div>
);

const getValueOption = (value, options) =>
  value
    ? options.find(option => option.get('value') === value) ||
      Map({
        label: value,
        value: value,
      })
    : null;

const SelectTypeaheadComponent = ({
  multiple,
  onChange: onChangeOrig,
  renderOptions,
  ...props
}) => {
  const components = useMemo(
    () => ({
      Input: generateInput({
        size: renderOptions.size,
        className: renderOptions.fieldClassName,
      }),
      Selection: (multiple
        ? generateMultipleSelection
        : generateSingleSelection)(selectionToValue, {
        size: renderOptions.size,
      }),
      SelectionsContainer: (multiple
        ? generateMultipleSelectionsContainer
        : generateSingleSelectionsContainer)({
        size: renderOptions.size,
      }),
    }),
    [multiple, renderOptions.size, renderOptions.fieldClassName],
  );

  const valueObject = useMemo(
    () =>
      multiple
        ? props.value.map(v => getValueOption(v, props.options))
        : getValueOption(props.value, props.options),
    [multiple, props.value, props.options],
  );

  const onChange = useCallback(
    v =>
      multiple
        ? onChangeOrig(v.map(option => option.get('value')))
        : onChangeOrig(v ? v.get('value') : ''),
    [multiple, onChangeOrig],
  );

  const { onNew: onNewOrig, onNewLabel } = renderOptions.custom;
  const onNew = useCallback(
    () => {
      if (typeof onNewOrig === 'function') {
        onNewOrig({
          afterComplete: newValue => {
            // After a new option is added, call the onChangeOrig fn
            if (newValue) {
              multiple
                ? onChangeOrig(props.value.push(newValue))
                : onChangeOrig(newValue);
            }
          },
        });
      }
    },
    [onNewOrig, multiple, onChangeOrig, props.value],
  );

  return (
    <FieldWrapper {...props} renderOptions={renderOptions} fieldset={multiple}>
      <StaticSelect
        allowNew={renderOptions.custom.allowNew}
        components={{
          ...components,
          Status,
          Suggestion,
          SuggestionAction,
          SuggestionsContainer,
        }}
        messages={{
          empty: 'No options found.',
          custom: 'No options found. Type to enter a custom option.',
        }}
        multiple={multiple}
        disabled={!props.enabled}
        id={props.id}
        value={valueObject}
        onChange={onChange}
        onBlur={props.onBlur}
        onFocus={props.onFocus}
        onNew={onNewOrig ? onNew : undefined}
        onNewLabel={onNewLabel}
        options={props.options}
        placeholder={renderOptions.placeholder}
        form={props.form}
        invalid={renderOptions.hasErrors}
      />
    </FieldWrapper>
  );
};

/******************************************************************************
 * Single Field
 ******************************************************************************/

const SelectComponent = ({ renderOptions, ...props }) => {
  // Get wrapper information if clear button can be visible
  const { Tag, props: tagProps, content } = getClearButtonWrapper(
    renderOptions,
    props,
  );
  const emptyOptionShown =
    !props.value || !renderOptions.custom.omitEmptyOption;
  const placeholderOptionShown =
    !props.value &&
    !props.focused &&
    !renderOptions.isFloating &&
    !!renderOptions.placeholder;
  // Should we show a temporary option for an invalid value that doesn't exist
  // in the options list
  const invalidValue =
    renderOptions.custom.showInvalidValue &&
    props.value &&
    !props.options.find(option => option.get('value') === props.value);

  return (
    <FieldWrapper {...props} renderOptions={renderOptions}>
      <Tag {...tagProps}>
        <select
          ref={props.focusRef}
          className={classNames('form-select', renderOptions.fieldClassName, {
            'form-select-sm': renderOptions.size === 'sm',
            'is-invalid': renderOptions.hasErrors || invalidValue,
            'has-btn': renderOptions.hasClear && !!props.value,
            'is-empty': !props.value,
            'text-muted': placeholderOptionShown,
          })}
          disabled={!props.enabled}
          id={props.id}
          name={props.name}
          value={props.value}
          onBlur={props.onBlur}
          onChange={props.onChange}
          onFocus={props.onFocus}
          form={props.form}
        >
          {invalidValue && (
            <option value={props.value} disabled>
              {props.value} (Invalid Value)
            </option>
          )}
          {emptyOptionShown && (
            <option value="">
              {placeholderOptionShown ? renderOptions.placeholder : ''}
            </option>
          )}
          {props.options.map((option, i) => (
            <option value={option.get('value')} key={i}>
              {option.get('label') ? option.get('label') : option.get('value')}
            </option>
          ))}
        </select>
        {content}
      </Tag>
    </FieldWrapper>
  );
};

export const SelectField = props => {
  const renderOptions = parseRenderOptions(props);
  return renderOptions.custom.typeahead ? (
    <SelectTypeaheadComponent renderOptions={renderOptions} {...props} />
  ) : (
    <SelectComponent renderOptions={renderOptions} {...props} />
  );
};

export const SelectFilter = props => {
  const renderOptions = parseRenderOptions(props, null, {
    clear: true,
    floating: true,
    size: 'sm',
  });
  return <SelectComponent renderOptions={renderOptions} {...props} />;
};

/******************************************************************************
 * Multi Field
 ******************************************************************************/

export class SelectMultiComponent extends Component {
  onEdit = index => event => {
    this.props.onChange(
      event.target.value
        ? this.props.value.set(index, event.target.value)
        : this.props.value.delete(index),
    );
  };

  onAdd = event => {
    this.props.onChange(this.props.value.push(event.target.value));
  };

  onRemove = index => () => {
    this.props.onChange(this.props.value.delete(index));
  };

  onFocus = index => e => {
    this.focusedIndex = index;
    this.props.onFocus(e);
  };

  render() {
    const { renderOptions, ...props } = this.props;

    return (
      <FieldWrapper {...props} renderOptions={renderOptions} fieldset>
        {props.value
          // If the field is enabled or has no values, append an empty string
          // for adding another value to the values list
          .concat(props.enabled || props.value.size === 0 ? [''] : [])
          .map((selection, i) => (
            <div className="field-floating" key={i}>
              <select
                id={props.id}
                className={classNames(
                  'form-select',
                  renderOptions.fieldClassName,
                  {
                    'form-select-sm': renderOptions.size === 'sm',
                    'is-invalid': !selection && renderOptions.hasErrors,
                    'has-btn': !!selection,
                    'is-empty': !selection,
                    'text-muted':
                      !selection &&
                      (!props.focused || this.focusedIndex !== i) &&
                      !!renderOptions.placeholder,
                  },
                )}
                disabled={!props.enabled}
                onBlur={props.onBlur}
                onChange={selection ? this.onEdit(i) : this.onAdd}
                onFocus={this.onFocus(i)}
                value={selection}
                form={props.form}
              >
                <option value="">
                  {(!selection &&
                    (!props.focused || this.focusedIndex !== i) &&
                    renderOptions.placeholder) ||
                    ''}
                </option>
                {props.options
                  .filter(
                    option =>
                      !props.value.includes(option.get('value')) ||
                      option.get('value') === selection,
                  )
                  .map((option, i) => (
                    <option key={i} value={option.get('value')}>
                      {option.get('label')}
                    </option>
                  ))}
              </select>
              {selection &&
                props.enabled && (
                  <button
                    type="button"
                    className={classNames('btn btn-link-primary btn-floating', {
                      'btn-sm': renderOptions.size === 'sm',
                    })}
                    onClick={this.onRemove(i)}
                    aria-label="Clear Value"
                    title="Clear Value"
                  >
                    <Icon name="x" md />
                  </button>
                )}
            </div>
          ))}
      </FieldWrapper>
    );
  }
}

export const SelectMultiField = props => {
  const renderOptions = parseRenderOptions(props, { isFloating: false });
  return renderOptions.custom.typeahead ? (
    <SelectTypeaheadComponent
      renderOptions={renderOptions}
      {...props}
      multiple={true}
    />
  ) : (
    <SelectMultiComponent renderOptions={renderOptions} {...props} />
  );
};

export const SelectMultiFilter = props => {
  const renderOptions = parseRenderOptions(
    props,
    { isFloating: false },
    { size: 'sm' },
  );
  return <SelectMultiComponent renderOptions={renderOptions} {...props} />;
};
